import { useEffect, useRef } from "react";

/**
 * Generates a minimal silent WAV as a data URI.
 * 1 second of silence at 8kHz, 8-bit mono (~8KB).
 *
 * Chrome pauses <video> elements in background tabs to save power ("video-only
 * background media"). A looping silent <audio> element signals the browser that
 * the tab is producing audio, which prevents this throttling.
 */
function createSilentWavDataUri(): string {
  const sampleRate = 8000;
  const numSamples = sampleRate; // 1 second
  const bytesPerSample = 1;
  const dataSize = numSamples * bytesPerSample;
  const headerSize = 44;
  const buffer = new ArrayBuffer(headerSize + dataSize);
  const view = new DataView(buffer);

  // RIFF header
  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, "WAVE");

  // fmt chunk
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true); // chunk size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * bytesPerSample, true); // byte rate
  view.setUint16(32, bytesPerSample, true); // block align
  view.setUint16(34, 8, true); // bits per sample

  // data chunk — silence is 128 for 8-bit PCM
  writeString(view, 36, "data");
  view.setUint32(40, dataSize, true);
  for (let i = 0; i < dataSize; i++) {
    view.setUint8(headerSize + i, 128);
  }

  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return "data:audio/wav;base64," + btoa(binary);
}

function writeString(view: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

let cachedSilentWav: string | null = null;
function getSilentWav(): string {
  if (!cachedSilentWav) {
    cachedSilentWav = createSilentWavDataUri();
  }
  return cachedSilentWav;
}

/**
 * Plays a looping silent WAV on a dedicated <audio> element while the MSE
 * player is active. This prevents Chrome from pausing the MSE <video> element
 * in background tabs.
 */
export function useBackgroundAudioKeepAlive({
  audioElement,
  active,
}: {
  audioElement: HTMLAudioElement | null;
  active: boolean;
}) {
  const wasActiveRef = useRef(false);

  useEffect(() => {
    if (!audioElement) return;

    if (active && !wasActiveRef.current) {
      audioElement.src = getSilentWav();
      audioElement.loop = true;
      audioElement.volume = 0.001;
      audioElement.play().catch(() => {
        // AbortError is expected if the element is cleaned up immediately
      });
      wasActiveRef.current = true;
    } else if (!active && wasActiveRef.current) {
      audioElement.pause();
      audioElement.removeAttribute("src");
      audioElement.load(); // reset the element
      wasActiveRef.current = false;
    }

    return () => {
      if (wasActiveRef.current) {
        audioElement.pause();
        audioElement.removeAttribute("src");
        wasActiveRef.current = false;
      }
    };
  }, [audioElement, active]);
}
